/**
 * Navigation Tool for CopilotKit
 *
 * Enables AI to navigate users to different pages with approval.
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import useApprovalKeyboardShortcuts from './useApprovalKeyboardShortcuts';
import { useHumanInTheLoop } from '@copilotkit/react-core';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { openDrawer } from '@/redux/slices/aiChatDrawerSlice';
import {
  generateAiNavigationDescription,
  generateAiNavigationEnum,
  generateAiPageMap,
} from '@/helpers/routeFormatters';
import { AIToolChip } from '@/components/AIChat/AIToolChip';
import { ApprovalButtons } from '@/components/AIChat/ApprovalButtons';
import { ToolDisplayStatus } from '@/pages/AIChatCopilotKit/types';
import { Flex } from '@electricjs/arc';
import { COPILOT_STATUS, TOOL_NAMES } from '@/helpers/aiChat/tools';

type NavigationToolRendererProps = {
  args: Record<string, unknown>;
  status: string;
  respond?: (result: string) => void;
  navigate: (path: string) => void;
  pageMap: Record<string, string>;
};

/**
 * Validates page argument from CopilotKit tool args
 */
const validateArgs = (
  args: Record<string, unknown>
): { page: string } | null => {
  if (typeof args.page !== 'string' || !args.page) {
    return null;
  }
  return { page: args.page };
};

/**
 * Renders navigation tool with approval UI and keyboard shortcuts
 */
const NavigationToolRenderer = ({
  args,
  status,
  respond,
  navigate,
  pageMap,
}: NavigationToolRendererProps) => {
  const [wasRejected, setWasRejected] = useState(false);

  // Validate args (must happen before hooks)
  const validatedArgs = validateArgs(args);
  const page = validatedArgs?.page;

  // Define handlers with useCallback to avoid recreating on every render
  const handleApprove = useCallback(() => {
    if (!respond || !page) return;

    // Map page name to route (auto-generated from metadata)
    const route = pageMap[page];

    if (!route) {
      respond(`Error: Unknown page "${page}"`);
      return;
    }

    // Navigate using React Router
    navigate(route);

    // Return confirmation message to the AI
    respond(`Successfully navigated to ${page} page at ${route}`);
  }, [respond, page, pageMap, navigate]);

  const handleReject = useCallback(() => {
    if (!respond) return;
    setWasRejected(true);
    respond('Navigation cancelled by user');
  }, [respond]);

  // Add keyboard shortcuts when awaiting approval
  useApprovalKeyboardShortcuts({
    onApprove: handleApprove,
    onDeny: handleReject,
    enabled: !!page && status === COPILOT_STATUS.EXECUTING && !!respond,
  });

  // Conditional rendering after all hooks
  if (!validatedArgs || !page) {
    if (respond && status === COPILOT_STATUS.EXECUTING) {
      respond('Error: Invalid page parameter');
    }
    return <></>;
  }

  // Map status to display status
  let displayStatus: ToolDisplayStatus;
  if (status === COPILOT_STATUS.EXECUTING) {
    displayStatus = ToolDisplayStatus.NEEDS_APPROVAL;
  } else if (status === COPILOT_STATUS.COMPLETE && wasRejected) {
    displayStatus = ToolDisplayStatus.REJECTED;
  } else if (status === COPILOT_STATUS.COMPLETE) {
    displayStatus = ToolDisplayStatus.COMPLETED;
  } else {
    displayStatus = ToolDisplayStatus.PENDING;
  }

  return (
    <Flex flexDirection="column">
      <AIToolChip
        id={`nav-${Date.now()}`}
        toolSlug="navigate_to_page"
        toolArgs={JSON.stringify(args)}
        variant={displayStatus}
      />
      {status === COPILOT_STATUS.EXECUTING && respond && (
        <ApprovalButtons
          onApprove={handleApprove}
          onReject={handleReject}
          style={{ flexDirection: 'row' }}
        />
      )}
    </Flex>
  );
};

/**
 * Registers CopilotKit navigation tool. Call in components where navigation is needed.
 *
 * @example
 * function MyPage() {
 *   useNavigationTool();
 *   return <div>Content</div>;
 * }
 */
export const useNavigationTool = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isDrawerOpen = useAppSelector(state => state.aiChatDrawer.isOpen);
  const isDrawerOpenRef = useRef(isDrawerOpen);
  useEffect(() => {
    isDrawerOpenRef.current = isDrawerOpen;
  }, [isDrawerOpen]);

  // Memoize pageMap since it's static and doesn't change during the session
  const pageMap = useMemo(() => generateAiPageMap(), []);

  // Read drawer state via ref so this callback stays stable across drawer open/close,
  // preventing renderNavigationTool and useHumanInTheLoop from re-registering the tool.
  const navigateWithDrawer = useCallback(
    (path: string) => {
      if (!isDrawerOpenRef.current) {
        dispatch(openDrawer());
      }
      navigate(path);
    },
    [navigate, dispatch]
  );

  // Memoize render function to prevent tool re-registration on every render
  const renderNavigationTool = useCallback(
    (props: {
      args: Record<string, unknown>;
      status: string;
      respond?: (result: string) => void;
    }) => (
      <NavigationToolRenderer
        {...props}
        navigate={navigateWithDrawer}
        pageMap={pageMap}
      />
    ),
    [navigateWithDrawer, pageMap]
  );

  useHumanInTheLoop({
    name: TOOL_NAMES.NAVIGATE_TO_PAGE,
    description:
      'Navigates the user to a different page. Call this tool immediately whenever the user explicitly asks to go to a page or the conversation is clearly about a topic tied to one of the available pages (e.g. MDM setup, ABM configuration, employee management, billing). Do NOT ask the user for confirmation before calling — the approval UI handles that. Do NOT use this tool when the user asks for product recommendations or wants to browse products - instead use product search tools and present results in chat.',
    parameters: [
      {
        name: 'page',
        type: 'string',
        description: generateAiNavigationDescription(),
        enum: generateAiNavigationEnum(),
        required: true,
      },
    ],
    render: renderNavigationTool,
  });
};
